"use client";

import { useState } from "react";
import { CheckCircle, Loader2, AlertTriangle } from "lucide-react";
import { formatDateTime } from "@/lib/formatting";
import { useDealApiMutation } from "@/lib/deals/useDealApiMutation";
import type { DealPayments, PaymentCheckboxKey } from "@/lib/deals/payment/types";
import {
  PAYMENT_CHECKBOX_LABELS,
  PAYMENT_CHECKBOX_DESCRIPTIONS,
  PAYMENT_CHECKBOX_KEYS,
} from "@/lib/deals/payment/types";

interface AdminPaymentPanelProps {
  dealId: string;
  payment: DealPayments | null;
  onPaymentUpdated?: (payment: DealPayments) => void;
  className?: string;
}

/**
 * AdminPaymentPanel - Admin controls for managing payment checkboxes
 */
export function AdminPaymentPanel({
  dealId,
  payment,
  onPaymentUpdated,
  className = "",
}: AdminPaymentPanelProps) {
  const [loadingKey, setLoadingKey] = useState<PaymentCheckboxKey | null>(null);
  const { mutate, error } = useDealApiMutation<DealPayments>({
    onSuccess: (data) => onPaymentUpdated?.(data),
  });

  const handleToggleCheckbox = async (checkbox: PaymentCheckboxKey) => {
    const currentValue = payment?.[checkbox] ?? false;
    setLoadingKey(checkbox);
    await mutate(`/api/admin/deals/${dealId}/payment`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "toggle_checkbox",
        checkbox,
        checked: !currentValue,
      }),
    });
    setLoadingKey(null);
  };

  const completedCount = payment ? PAYMENT_CHECKBOX_KEYS.filter((key) => payment[key]).length : 0;

  return (
    <div className={`rounded-lg border border-zinc-200 bg-white p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-zinc-900">Zahlungsverwaltung</h3>
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${
            completedCount === PAYMENT_CHECKBOX_KEYS.length
              ? "bg-emerald-100 text-emerald-700"
              : "bg-amber-100 text-amber-700"
          }`}
        >
          {completedCount}/{PAYMENT_CHECKBOX_KEYS.length} erledigt
        </span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 rounded-lg flex items-center gap-2 text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Payment checkboxes */}
      <div className="space-y-2">
        {PAYMENT_CHECKBOX_KEYS.map((key) => {
          const isChecked = payment?.[key] ?? false;
          const checkedAt = payment?.[`${key}_at` as keyof DealPayments] as string | null;
          const isLoading = loadingKey === key;

          return (
            <button
              key={key}
              onClick={() => handleToggleCheckbox(key)}
              disabled={isLoading}
              className={`w-full flex items-start gap-3 p-3 rounded-lg text-left transition-colors ${
                isChecked ? "bg-emerald-50 hover:bg-emerald-100" : "bg-zinc-50 hover:bg-zinc-100"
              } disabled:opacity-50`}
            >
              <div
                className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  isChecked ? "bg-emerald-500 text-white" : "bg-white border-2 border-zinc-300"
                }`}
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isChecked ? (
                  <CheckCircle className="w-4 h-4" />
                ) : null}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    isChecked ? "text-emerald-700" : "text-zinc-700"
                  }`}
                >
                  {PAYMENT_CHECKBOX_LABELS[key]}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">{PAYMENT_CHECKBOX_DESCRIPTIONS[key]}</p>
                {isChecked && checkedAt && (
                  <p className="text-xs text-zinc-400 mt-1">
                    Erledigt am {formatDateTime(checkedAt)}
                  </p>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {completedCount === PAYMENT_CHECKBOX_KEYS.length && (
        <div className="mt-4 p-3 bg-emerald-100 rounded-lg border border-emerald-200">
          <p className="text-sm text-emerald-800 font-medium text-center">
            Alle Zahlungen abgeschlossen - Deal wird automatisch als abgeschlossen markiert.
          </p>
        </div>
      )}
    </div>
  );
}
